import { useState, useEffect, useMemo, useContext, useCallback } from "react";
import { usePageTitle } from "../../../hooks/useMeta";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  updateUser,
  updateRestaurantValues,
} from "../../../redux/slices/userSlice";
import { updateControlledRestaurantConsumptionModes } from "../../../redux/slices/adminSlice";
import { toast } from "../../../utils/demoToast";
import { isDemoMode } from "../../../utils/demoApiInterceptor";
import useApi from "../../../hooks/useApi";
import Tabs from "../../tabs/Tabs";
import Profile from "../settings/restaurant/Profile";
import Images from "../settings/restaurant/Images";
//import Delivery from '../settings/restaurant/Delivery';
import Catering from "../settings/restaurant/Catering";
import Service from "../settings/restaurant/Service";
import Bank from "../settings/restaurant/Bank";
import Manager from "../settings/restaurant/Manager";
import Connection from "../settings/Connection";
import DangerZone from "../settings/restaurant/DangerZone";
import {
  getRestaurantSettings,
  updateRestaurantSettings,
} from "../../../services/restaurantService";
import { updateRestaurantSettingsByAdmin } from "../../../services/admin/restaurantService";
import {
  getRestaurantUserDetail,
  updateRestaurantEmployee,
} from "../../../services/userService";
import { putAttemptChangePassword } from "../../../services/authService";
import SettingsContext from "../../../contexts/SettingsContext";
import useBreakpoint from "../../../hooks/useBreakpoint";
import { useSubscription } from "../../../hooks/useSubscription";
import { useParams } from "react-router-dom";
import useLangNavigate from "../../../hooks/useLangNavigate";
import Button from "../../buttons/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave } from "@fortawesome/pro-light-svg-icons";
import Spinner from "../../utils/Spinner";

export const RestaurantSettings = ({ tKey = "dashboard.settings." }) => {
  usePageTitle("restaurantSettings");
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { hasActiveModule } = useSubscription();
  const [manager, setManager] = useState({});
  const [restaurantSettings, setRestaurantSettings] = useState({});
  const [tempManagerSettings, setTempManagerSettings] = useState({});
  const [tempRestaurantSettings, setTempRestaurantSettings] = useState({});
  const [tempPassword, setTempPassword] = useState({});
  const { tabName } = useParams();
  const navigate = useLangNavigate();
  const { request: getSettingsRequest, loading: settingsLoading } = useApi(
    getRestaurantSettings,
  );
  const { request: getManagerRequest, loading: managerLoading } = useApi(
    getRestaurantUserDetail,
  );
  const { request: updateSettingsRequest, loading: updateSettingsLoading } =
    useApi(updateRestaurantSettings);
  const {
    request: updateSettingsByAdminRequest,
    loading: adminUpdateSettingsLoading,
  } = useApi(updateRestaurantSettingsByAdmin);
  const { request: updateManagerRequest, loading: updateManagerLoading } =
    useApi(updateRestaurantEmployee);
  const {
    request: attemptChangePasswordRequest,
    loading: updatePasswordLoading,
  } = useApi(putAttemptChangePassword);
  const user = useSelector((state) => state.user);
  const isControllingEntity = useSelector(
    (state) => state.admin.isControllingEntity,
  );
  const {
    activeTab,
    setActiveTab,
    setIsValid,
    setApplyChanges,
    setHideSettingsButton,
    isValid,
  } = useContext(SettingsContext);
  const { isLtLaptop } = useBreakpoint();

  const handleFormDataChange = useCallback(
    (data) => {
      setIsValid(!!data);
      setTempRestaurantSettings((old) => {
        return { ...old, ...data };
      });
    },
    [setIsValid, setTempRestaurantSettings],
  );

  const handleManagerDataChange = useCallback(
    (data) => {
      setIsValid(!!data);
      setTempManagerSettings((old) => {
        return { ...old, ...data };
      });
    },
    [setIsValid, setTempManagerSettings],
  );

  const handlePasswordChange = useCallback(
    (data) => {
      setIsValid(!!data);
      setTempPassword(data);
    },
    [setIsValid, setTempPassword],
  );

  const tabs = useMemo(
    () => [
      {
        name: "profile",
        content: () => (
          <Profile
            data={restaurantSettings}
            onFormDataChange={handleFormDataChange}
          />
        ),
      },
      {
        name: "images",
        content: () => (
          <Images
            data={restaurantSettings}
            onFormDataChange={handleFormDataChange}
          />
        ),
      },
      /*
    {
      name: 'delivery',
      content: () => <Delivery data={restaurantSettings} onFormDataChange={handleFormDataChange} />,
    },
    */
      {
        name: "catering",
        hide: !restaurantSettings.consumptionmodes?.find(
          (mode) => mode.consumptionModeId === 5,
        ),
        content: () => (
          <Catering
            data={restaurantSettings}
            onFormDataChange={handleFormDataChange}
          />
        ),
      },
      {
        name: "service",
        hide: !hasActiveModule("tables") && !hasActiveModule("booking"),
        content: () => <Service />,
      },
      {
        name: "bank",
        hide: isDemoMode(),
        content: () => <Bank restaurantId={user.restaurantId} />,
      },
      {
        name: "manager",
        content: () => (
          <Manager data={manager} onFormDataChange={handleManagerDataChange} />
        ),
      },
      {
        name: "connection",
        hide: isDemoMode(),
        content: () => <Connection onFormDataChange={handlePasswordChange} />,
      },
      {
        name: "dangerZone",
        hide: isDemoMode(),
        content: () => <DangerZone />,
        buttonVariant: "danger",
      },
    ],
    [
      restaurantSettings,
      handleFormDataChange,
      handleManagerDataChange,
      handlePasswordChange,
      manager,
      user.restaurantId,
      hasActiveModule,
    ],
  );

  const fetchSettingsRestaurant = useCallback(
    async (restaurantId) => {
      await getSettingsRequest(restaurantId)
        .then((response) => {
          if (isControllingEntity)
            dispatch(
              updateControlledRestaurantConsumptionModes(
                response.data.consumptionmodes,
              ),
            );
          else {
            dispatch(
              updateRestaurantValues({
                consumptionmodes: response.data.consumptionmodes,
                fullImagePath: response.data.images?.[0]?.fullImagePath,
              }),
            );
          }

          setRestaurantSettings(response.data);
          setManager(response.data.manager[0] || null);
        })
        .catch((error) => {
          console.log(error);
        });
    },
    [getSettingsRequest, setRestaurantSettings, dispatch, isControllingEntity],
  );

  const fetchSettingsManager = useCallback(
    async (userId) => {
      await getManagerRequest(userId)
        .then((response) => {
          dispatch(updateUser(response.data));
        })
        .catch((error) => {
          console.log(error);
        });
    },
    [getManagerRequest, dispatch],
  );

  const attemptUpdateProfile = useCallback(async () => {
    await updateSettingsRequest(user.restaurantId, tempRestaurantSettings)
      .then(async () => {
        toast.success(t(`${tKey}success`));
        await fetchSettingsRestaurant(user.restaurantId);
      })
      .catch((error) => {
        // Handle address validation error
        if (error.message === "ADDRESS_NOT_FOUND") {
          toast.error(t(`${tKey}profile.errors.address.notFound`));
        } else {
          toast.error(
            error.message ||
              "An error occurred while saving settings. Please try again.",
          );
        }
        console.error(error);
      });
  }, [
    updateSettingsRequest,
    user.restaurantId,
    tempRestaurantSettings,
    t,
    tKey,
    fetchSettingsRestaurant,
  ]);

  const attemptUpdateProfileByAdmin = useCallback(async () => {
    await updateSettingsByAdminRequest(
      user.restaurantId,
      tempRestaurantSettings,
    )
      .then(async () => {
        toast.success(t(`${tKey}success`));
        await fetchSettingsRestaurant(user.restaurantId);
      })
      .catch((error) => {
        // Handle address validation error
        if (error.message === "ADDRESS_NOT_FOUND") {
          toast.error(t(`${tKey}profile.errors.address.notFound`));
        } else {
          toast.error(
            error.message ||
              "An error occurred while saving settings. Please try again.",
          );
        }
        console.error(error);
      });
  }, [
    updateSettingsByAdminRequest,
    user.restaurantId,
    tempRestaurantSettings,
    t,
    tKey,
    fetchSettingsRestaurant,
  ]);

  const attemptUpdateManager = useCallback(async () => {
    if (manager)
      await updateManagerRequest(manager.userId, tempManagerSettings).then(
        async () => {
          toast.success(t(`${tKey}success`));
          await fetchSettingsManager(manager.userId);
        },
      );
  }, [
    updateManagerRequest,
    manager,
    tempManagerSettings,
    t,
    tKey,
    fetchSettingsManager,
  ]);

  const attemptChangePassword = useCallback(async () => {
    try {
      await attemptChangePasswordRequest(user.userId, tempPassword);
      toast.success(t(`${tKey}success`));
    } catch (error) {
      // Prevent error from propagating and causing blocking error
      // Handle invalid credentials error (401 status or specific error message)
      const errorMessage = error?.message || "";
      const errorStatus = error?.status;

      if (
        errorStatus === 401 ||
        errorMessage.includes("INVALID_CREDENTIALS") ||
        errorMessage.includes("Identifiants invalides") ||
        errorMessage === "Identifiants invalides"
      ) {
        toast.error(t(`${tKey}connection.errors.oldPassword.invalid`));
      } else {
        toast.error(
          errorMessage || t(`${tKey}success`).replace("successfully", "failed"),
        );
      }
      // Don't re-throw the error to prevent blocking error display
      // Error is already handled and displayed as toast
    }
  }, [attemptChangePasswordRequest, user.userId, tempPassword, t, tKey]);

  const applyChanges = useCallback(async () => {
    try {
      switch (activeTab) {
        case "bank":
        case "service":
          return null;
        case "manager":
          return manager ? await attemptUpdateManager() : null;
        case "connection":
          return await attemptChangePassword();
        case "dangerZone":
          return;
        default:
          return isControllingEntity
            ? await attemptUpdateProfileByAdmin()
            : await attemptUpdateProfile();
      }
    } catch (error) {
      // Error is already handled in individual functions, just prevent unhandled rejection
      // Don't log or re-throw to prevent blocking error display
    }
  }, [
    activeTab,
    manager,
    attemptChangePassword,
    attemptUpdateManager,
    attemptUpdateProfile,
    attemptUpdateProfileByAdmin,
    isControllingEntity,
  ]);

  const setActiveTabWrapper = useCallback(
    (tab) => {
      setHideSettingsButton(tab === "dangerZone" || tab === "service");
      setActiveTab(tab);
    },
    [setActiveTab, setHideSettingsButton],
  );

  const visibleTabNames = useMemo(
    () => tabs.filter((tab) => !tab.hide).map((tab) => tab.name),
    [tabs],
  );

  const defaultTab = useMemo(() => {
    if (visibleTabNames.includes("profile")) return "profile";
    return visibleTabNames[0] || null;
  }, [visibleTabNames]);

  const resolvedTab = useMemo(() => {
    if (!defaultTab) return null;
    return tabName && visibleTabNames.includes(tabName) ? tabName : defaultTab;
  }, [tabName, visibleTabNames, defaultTab]);

  const handleTabChange = useCallback(
    (tab) => {
      setActiveTabWrapper(tab);
      navigate(`/dashboard/settings/${tab}`);
    },
    [setActiveTabWrapper, navigate],
  );

  useEffect(() => {
    fetchSettingsRestaurant(user.restaurantId);
    setHideSettingsButton(false);
    return () => {
      setHideSettingsButton(true);
    };
  }, [
    user.restaurantId,
    fetchSettingsRestaurant,
    setHideSettingsButton,
  ]);

  useEffect(() => {
    if (!resolvedTab) return;
    setActiveTabWrapper(resolvedTab);
    if (tabName !== resolvedTab) {
      navigate(`/dashboard/settings/${resolvedTab}`, { replace: true });
    }
  }, [resolvedTab, tabName, setActiveTabWrapper, navigate]);

  useEffect(() => {
    setApplyChanges(() => applyChanges);
  }, [applyChanges, setApplyChanges]);

  useEffect(() => {
    if (
      updateManagerLoading ||
      updateSettingsLoading ||
      updatePasswordLoading ||
      adminUpdateSettingsLoading
    )
      setIsValid(false);
  }, [
    updateManagerLoading,
    updateSettingsLoading,
    updatePasswordLoading,
    adminUpdateSettingsLoading,
    setIsValid,
  ]);

  return (
    <>
      <div id="settings" className="dashboard-content">
        {settingsLoading || managerLoading ? (
          <Spinner />
        ) : (
          <>
            <div data-tour="settings-tabs">
              <Tabs
                preventSwitchTab={true}
                tKey={tKey}
                tKeySuffix=".tab"
                tabs={tabs}
                activeTab={resolvedTab || undefined}
                onTabChange={handleTabChange}
                contentClass="flex gap-1 settings-content"
              ></Tabs>
            </div>
            {isLtLaptop() && (
              <Button
                onClick={applyChanges}
                floating
                variant="success"
                disabled={!isValid}
                className="flex gap-1"
              >
                <FontAwesomeIcon size="2x" icon={faSave} />
                <span>{t(`utils.common.save`)}</span>
              </Button>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default RestaurantSettings;
